import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TopNavigation from '../../components/feature/TopNavigation';
import BottomNavigation from '../../components/feature/BottomNavigation';
import Card from '../../components/base/Card';
import Button from '../../components/base/Button';

interface Doctor {
  id: string;
  name: string;
  specialty: string;
  rating: number;
  experience: string;
  price: number;
  image: string;
  availableTimes: string[];
}

interface MedicalCenter {
  id: string;
  name: string;
  location: string;
  rating: number;
  distance: string;
}

const mockDoctors: Doctor[] = [
  {
    id: '1',
    name: 'د. أحمد محمد الشيخ',
    specialty: 'طب الأسرة',
    rating: 4.8,
    experience: '15 سنة خبرة',
    price: 150,
    image: 'https://public.readdy.ai/ai/img_res/b85a47aef277b2afa5ff94e2f7eb0cff.jpg',
    availableTimes: ['09:00', '10:30', '14:00', '15:30', '17:00']
  },
  {
    id: '2',
    name: 'د. فاطمة أحمد النور',
    specialty: 'طب النساء والولادة',
    rating: 4.9,
    experience: '12 سنة خبرة',
    price: 200,
    image: 'https://readdy.ai/api/search-image?query=Professional%20female%20doctor%20in%20white%20coat%2C%20middle%20eastern%20appearance%2C%20confident%20smile%2C%20medical%20clinic%20background%2C%20professional%20headshot%2C%20clean%20lighting%2C%20medical%20professional%20portrait&width=100&height=100&seq=2&orientation=squarish',
    availableTimes: ['08:30', '11:00', '13:30', '16:00']
  },
  {
    id: '3',
    name: 'د. محمد عبدالله الحسن',
    specialty: 'طب الأطفال',
    rating: 4.7,
    experience: '18 سنة خبرة',
    price: 180,
    image: 'https://readdy.ai/api/search-image?query=Professional%20male%20pediatrician%20doctor%20in%20white%20coat%2C%20middle%20eastern%20appearance%2C%20warm%20smile%2C%20pediatric%20clinic%20background%2C%20professional%20headshot%2C%20clean%20lighting%2C%20medical%20professional%20portrait&width=100&height=100&seq=3&orientation=squarish',
    availableTimes: ['09:30', '11:30', '14:30', '16:30']
  }
];

const mockCenters: MedicalCenter[] = [
  {
    id: '1',
    name: 'مستشفى الملك فهد',
    location: 'الرياض - حي الملز',
    rating: 4.6,
    distance: '2.5 كم'
  },
  {
    id: '2',
    name: 'مجمع عيادات النور الطبي',
    location: 'الرياض - حي العليا',
    rating: 4.8,
    distance: '3.2 كم'
  },
  {
    id: '3',
    name: 'مستشفى الدكتور سليمان الحبيب',
    location: 'الرياض - حي الصحافة',
    rating: 4.9,
    distance: '4.1 كم'
  }
];

export default function BookingPage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedService, setSelectedService] = useState('');
  const [selectedCenter, setSelectedCenter] = useState('');
  const [selectedDoctor, setSelectedDoctor] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');

  const services = [
    { id: 'consultation', name: 'استشارة طبية', icon: 'ri-stethoscope-line', price: 150 },
    { id: 'lab', name: 'تحاليل مختبرية', icon: 'ri-test-tube-line', price: 80 },
    { id: 'xray', name: 'أشعة تشخيصية', icon: 'ri-scan-line', price: 120 },
    { id: 'therapy', name: 'علاج طبيعي', icon: 'ri-heart-pulse-line', price: 100 }
  ];

  const generateDates = () => {
    const dates = [];
    const today = new Date();
    for (let i = 0; i < 7; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push({
        value: date.toISOString().split('T')[0],
        label: date.toLocaleDateString('ar-SA', { weekday: 'short', day: 'numeric', month: 'short' }),
        isToday: i === 0
      });
    }
    return dates;
  };

  const handleNext = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBookingConfirmation = () => {
    // إتمام الحجز والتوجه لصفحة تأكيد الحجز
    const confirmed = window.confirm('هل أنت متأكد من تأكيد هذا الحجز؟');
    if (confirmed) {
      // التوجه لصفحة تأكيد الحجز
      navigate('/booking-success');
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    } else {
      navigate('/');
    }
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-6">
      {[1, 2, 3, 4].map((step) => (
        <div key={step} className="flex items-center">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
            step <= currentStep ? 'bg-[#007AFF] text-white' : 'bg-gray-200 text-gray-500'
          }`}>
            {step}
          </div>
          {step < 4 && (
            <div className={`w-8 h-0.5 mx-2 ${
              step < currentStep ? 'bg-[#007AFF]' : 'bg-gray-200'
            }`}></div>
          )}
        </div>
      ))}
    </div>
  );

  const renderStep1 = () => (
    <div>
      <h2 className="text-xl font-bold text-[#333333] mb-4">اختر نوع الخدمة</h2>
      <div className="grid grid-cols-2 gap-4">
        {services.map((service) => (
          <Card
            key={service.id}
            padding="md"
            onClick={() => setSelectedService(service.id)}
            className={`text-center cursor-pointer transition-all ${
              selectedService === service.id ? 'ring-2 ring-[#007AFF] bg-[#007AFF]/5' : ''
            }`}
          >
            <div className="w-12 h-12 bg-[#007AFF]/10 rounded-xl mx-auto mb-3 flex items-center justify-center">
              <i className={`${service.icon} text-xl text-[#007AFF]`}></i>
            </div>
            <h3 className="font-bold text-[#333333] text-sm mb-1">{service.name}</h3>
            <p className="text-[#7A7A7A] text-xs">{service.price} ريال</p>
          </Card>
        ))}
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div>
      <h2 className="text-xl font-bold text-[#333333] mb-4">اختر المركز الطبي</h2>
      <div className="space-y-3">
        {mockCenters.map((center) => (
          <Card
            key={center.id}
            padding="md"
            onClick={() => setSelectedCenter(center.id)}
            className={`cursor-pointer transition-all ${
              selectedCenter === center.id ? 'ring-2 ring-[#007AFF] bg-[#007AFF]/5' : ''
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h3 className="font-bold text-[#333333] mb-1">{center.name}</h3>
                <p className="text-[#7A7A7A] text-sm mb-2">{center.location}</p>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-1">
                    <i className="ri-star-fill text-yellow-400 text-sm"></i>
                    <span className="text-sm font-medium">{center.rating}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <i className="ri-map-pin-line text-[#7A7A7A] text-sm"></i>
                    <span className="text-sm text-[#7A7A7A]">{center.distance}</span>
                  </div>
                </div>
              </div>
              <div className="w-6 h-6 flex items-center justify-center">
                {selectedCenter === center.id && (
                  <i className="ri-check-line text-[#007AFF] text-lg"></i>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div>
      <h2 className="text-xl font-bold text-[#333333] mb-4">اختر الطبيب</h2>
      <div className="space-y-3">
        {mockDoctors.map((doctor) => (
          <Card
            key={doctor.id}
            padding="md"
            onClick={() => setSelectedDoctor(doctor.id)}
            className={`cursor-pointer transition-all ${
              selectedDoctor === doctor.id ? 'ring-2 ring-[#007AFF] bg-[#007AFF]/5' : ''
            }`}
          >
            <div className="flex items-center gap-3">
              <img
                src={doctor.image}
                alt={doctor.name}
                className="w-16 h-16 rounded-full object-cover"
              />
              <div className="flex-1">
                <h3 className="font-bold text-[#333333] mb-1">{doctor.name}</h3>
                <p className="text-[#007AFF] text-sm mb-1">{doctor.specialty}</p>
                <div className="flex items-center gap-4 mb-2">
                  <div className="flex items-center gap-1">
                    <i className="ri-star-fill text-yellow-400 text-sm"></i>
                    <span className="text-sm font-medium">{doctor.rating}</span>
                  </div>
                  <span className="text-sm text-[#7A7A7A]">{doctor.experience}</span>
                </div>
                <p className="text-[#34C759] font-bold text-sm">{doctor.price} ريال</p>
              </div>
              <div className="w-6 h-6 flex items-center justify-center">
                {selectedDoctor === doctor.id && (
                  <i className="ri-check-line text-[#007AFF] text-lg"></i>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );

  const renderStep4 = () => {
    const dates = generateDates();
    const selectedDoctorData = mockDoctors.find(d => d.id === selectedDoctor);
    
    return (
      <div>
        <h2 className="text-xl font-bold text-[#333333] mb-4">اختر التاريخ والوقت</h2>
        
        {/* Date Selection */}
        <div className="mb-6">
          <h3 className="font-medium text-[#333333] mb-3">التاريخ</h3>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {dates.map((date) => (
              <button
                key={date.value}
                onClick={() => setSelectedDate(date.value)}
                className={`min-w-[80px] p-3 rounded-xl text-center transition-all ${
                  selectedDate === date.value
                    ? 'bg-[#007AFF] text-white'
                    : 'bg-white border border-gray-200 text-[#333333]'
                }`}
              >
                <div className="text-xs mb-1">{date.label.split(' ')[0]}</div>
                <div className="font-bold">{date.label.split(' ')[1]}</div>
                {date.isToday && <div className="text-xs mt-1">اليوم</div>}
              </button>
            ))}
          </div>
        </div>

        {/* Time Selection */}
        {selectedDate && (
          <div>
            <h3 className="font-medium text-[#333333] mb-3">الوقت المتاح</h3>
            <div className="grid grid-cols-3 gap-3">
              {selectedDoctorData?.availableTimes.map((time) => (
                <button
                  key={time}
                  onClick={() => setSelectedTime(time)}
                  className={`p-3 rounded-xl text-center transition-all ${
                    selectedTime === time
                      ? 'bg-[#007AFF] text-white'
                      : 'bg-white border border-gray-200 text-[#333333]'
                  }`}
                >
                  {time}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Booking Summary */}
        {selectedDate && selectedTime && (
          <Card padding="md" className="mt-6 bg-[#007AFF]/5">
            <h3 className="font-bold text-[#333333] mb-3">ملخص الحجز</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-[#7A7A7A]">الخدمة:</span>
                <span className="font-medium">{services.find(s => s.id === selectedService)?.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#7A7A7A]">الطبيب:</span>
                <span className="font-medium">{selectedDoctorData?.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#7A7A7A]">التاريخ:</span>
                <span className="font-medium">{dates.find(d => d.value === selectedDate)?.label}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#7A7A7A]">الوقت:</span>
                <span className="font-medium">{selectedTime}</span>
              </div>
              <div className="border-t pt-2 mt-3">
                <div className="flex justify-between">
                  <span className="font-bold">المجموع:</span>
                  <span className="font-bold text-[#34C759]">{selectedDoctorData?.price} ريال</span>
                </div>
              </div>
            </div>
          </Card>
        )}
      </div>
    );
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1: return selectedService;
      case 2: return selectedCenter;
      case 3: return selectedDoctor;
      case 4: return selectedDate && selectedTime;
      default: return false;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <TopNavigation 
        title="حجز موعد" 
        showBack 
        onBack={handleBack}
      />
      
      <div className="pt-20 pb-24 px-4">
        {renderStepIndicator()}
        
        <div className="mb-6">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}
        </div>

        {/* Navigation Buttons */}
        <div className="flex gap-3">
          {currentStep > 1 && (
            <Button
              variant="outline"
              size="lg"
              onClick={handleBack}
              className="flex-1"
            >
              السابق
            </Button>
          )}
          <Button
            variant="primary"
            size="lg"
            onClick={currentStep === 4 ? handleBookingConfirmation : handleNext}
            disabled={!canProceed()}
            className="flex-1"
          >
            {currentStep === 4 ? 'تأكيد الحجز' : 'التالي'}
          </Button>
        </div>
      </div>
      
      <BottomNavigation />
    </div>
  );
}
